<template>
  <!-- Grid layout (when rowspan is used) -->
  <div v-if="panelVisible && useGrid" class="panel-grid-item">
    <MExpansion :class="{ 'is-simple': isSimple }" :title="panelTitle" :panel-id="panelId" :model-value="isOpenDefault" @update:modelValue="updateStoragePanelsState"
                :hide-header="hideHeader" :p-group-style="isPGroup" :draggable="panelDraggable">
      <template v-if="hasIdentificators">
        <Identificators v-for="(identificator, index) in identificators" :key="`identificator-${index}`"
                        :data="identificator" />
      </template>

      <!-- Panel-level top: Swing NORTH / FlowLayout.RIGHT — не смешивать с left -->
      <ActionsRow v-if="hasPanelTopActions" :config="data" :position="['top']" level="panel"
                  class="panel-actions-top" />

      <!-- Element-level top (layout.id = panelId) -->
      <ActionsRow v-if="hasElementTopActions" :config="data" :position="['top']" level="element"
                  class="element-actions-top" />

      <MPanelContent :rows="data.rows" class="panel-content-main" :style="panelContentStyle" />

      <ActionsRow v-if="hasElementBottomRightActions" :config="data" :position="['bottom', 'right']" level="element"
                  class="element-actions-bottom" />

      <!-- Panel-level bottom actions (inside expansion, after element actions) -->
      <ActionsRow v-if="hasPanelBottomActions" :config="data" :position="['bottom', 'right']" level="panel"
                  class="panel-actions-bottom" />
    </MExpansion>
  </div>

  <!-- Flexbox layout (original) -->
  <MCol v-else-if="panelVisible" :cols="cols">
    <MExpansion :class="{ 'is-simple': isSimple }" :title="panelTitle" :panel-id="panelId" :model-value="isOpenDefault" @update:modelValue="updateStoragePanelsState"
                :hide-header="hideHeader" :p-group-style="isPGroup" :draggable="panelDraggable">
      <template v-if="hasIdentificators">
        <Identificators v-for="(identificator, index) in identificators" :key="`identificator-${index}`"
                        :data="identificator" />
      </template>

      <!-- Panel-level top: Swing NORTH / FlowLayout.RIGHT — не смешивать с left -->
      <ActionsRow v-if="hasPanelTopActions" :config="data" :position="['top']" level="panel"
                  class="panel-actions-top" />

      <!-- Element-level top (layout.id = panelId) -->
      <ActionsRow v-if="hasElementTopActions" :config="data" :position="['top']" level="element"
                  class="element-actions-top" />

      <MPanelContent :rows="data.rows" class="panel-content-main" :style="panelContentStyle" />

      <ActionsRow v-if="hasElementBottomRightActions" :config="data" :position="['bottom', 'right']" level="element"
                  class="element-actions-bottom" />

      <!-- Panel-level bottom actions (inside expansion, after element actions) -->
      <ActionsRow v-if="hasPanelBottomActions" :config="data" :position="['bottom', 'right']" level="panel"
                  class="panel-actions-bottom" />
    </MExpansion>
  </MCol>
</template>

<script setup lang="ts">
import { useListeners } from '@/common/composables/useListeners'
import { usePageData } from '@/common/composables/usePageData'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { evaluateLayoutCondition } from '@/common/utils/evaluateLayoutCondition'
import { resolveInlineElementPanelVisible } from '@/common/utils/resolveInlineElementPanelVisible'
import { resolvePanelVisible } from '@/common/utils/resolvePanelVisible'
import {
  ElementContextKey,
  FormOverlayRemeasureKey,
  FormProviderKey,
  RegisterPanelKey,
  SwingTilesEmbedKey,
  SwingTilesRowScopeKey,
  UnRegisterPanelKey,
} from '@/components/platform/injection-keys'
import MCol from '@/components/ui/MCol.vue'
import MExpansion from '@/components/ui/MExpansion.vue'
import { computed, getCurrentInstance, inject, onMounted, onUnmounted, provide, reactive, ref, watch } from 'vue'
import type { PanelData } from '../types'
import ActionsRow from './ActionsRow.vue'
import MPanelContent from './Content.vue'
import Identificators from './Identificators.vue'
import { useSettingsStore } from '@/stores/settings'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
interface Props {
  data: PanelData
  pageId: string | number
}

const props = defineProps<Props>()

const registerPanel = inject(RegisterPanelKey)
const unRegisterPanel = inject(UnRegisterPanelKey)
const formProvider = inject(FormProviderKey, null)
// Панель рендерится как клон внутри строки Tiles (не как обычная top-level панель формы).
const swingTilesEmbed = inject(SwingTilesEmbedKey, false)
// Контекст текущей строки Tiles: tableFieldName/rowIndex и row-scoped доступ к данным.
const swingTilesRowScope = inject(SwingTilesRowScopeKey, null)
const overlayRemeasure = inject(FormOverlayRemeasureKey, null)
const useGrid = inject('useGridLayout', false)
const { pageData, pageActions, pageDataSlot, pageRegistryFormId, pageStates } = usePageData()
const { invokeListeners } = useListeners()

const {setPanelSetting, getPanelSetting} = useSettingsStore()
const pagesDataSlotId = computed(() => {
  return String(pageDataSlot.value ?? '').trim()
})
const resolvedFormId = computed(() => {
  if (pageRegistryFormId.value) return pageRegistryFormId.value
  const pd = pageData.value as any
  return resolveElementRegistryFormId(pd, pagesDataSlotId.value)
})
const elementContext = reactive({
  formId: resolvedFormId.value || props.data.formId,
  pageId: props.pageId,
  pageDataSlot: pagesDataSlotId.value,
  panelId: props.data.panelId,
  isReadOnly: props.data._attributes?.isReadOnly === "1",
  jsName: props.data._attributes?.jsName || props.data.id
})
const registeredFormId = ref<string | null>(null)
const panelRuntimeId = String(getCurrentInstance()?.uid ?? `panel-${props.data.panelId}`)

const tilesInstanceKey = () =>
  swingTilesRowScope
    ? `${swingTilesRowScope.tableFieldName}__row${swingTilesRowScope.rowIndex}`
    : undefined

// For split pages, use the page instance id (synthetic) so registry is isolated per document
watch(
  resolvedFormId,
  (fid) => {
    elementContext.formId = fid || props.data.formId
  },
  { immediate: true }
)

watch(
  pagesDataSlotId,
  (slot) => {
    elementContext.pageDataSlot = slot
  },
  { immediate: true },
)

provide(ElementContextKey, elementContext)
provide('panelId', String(props.data.panelId ?? ''))


// Computed properties
const panelConditionAttr = computed(() => String(props.data._attributes?.condition ?? '').trim())
const conditionVisible = ref(true)
const visibilityOverride = ref<boolean | null>(null)
let lastPanelConditionEvaluation = 0

const panelStateVisible = computed(() => {
  const id = panelId.value
  for (const state of pageStates.value ?? []) {
    if (String(state?.element_level ?? '').toUpperCase() !== 'PANEL') continue
    if (state?.is_visible === undefined) continue
    if (state.element_id != null && Number(state.element_id) === id) {
      return state.is_visible !== false
    }
  }
  return null
})

const panelVisible = computed(() => {
  return resolvePanelVisible({
    conditionVisible: conditionVisible.value,
    visibilityOverride: visibilityOverride.value,
    panelStateVisible: panelStateVisible.value,
    inlineVisible: resolveInlineElementPanelVisible({
      inlineElement: props.data._inlineElement === true,
      panelId: props.data.panelId,
      elementShellVisibleByElementId: formProvider?.elementShellVisibleByElementId?.value,
      baseVisible: true,
    }),
  })
})

const evaluatePanelCondition = async () => {
  const rawCondition = panelConditionAttr.value
  if (!rawCondition) {
    conditionVisible.value = true
    return
  }

  const evaluationId = ++lastPanelConditionEvaluation
  const formId = elementContext.formId || resolvedFormId.value || props.data.formId
  const visible = await evaluateLayoutCondition({
    condition: rawCondition,
    formProvider: formProvider as Parameters<typeof evaluateLayoutCondition>[0]['formProvider'],
    pageData: pageData.value,
    formId,
    panelId: props.data.panelId,
    measureLabel: 'Panel.condition.evaluate',
  })
  if (evaluationId !== lastPanelConditionEvaluation) {
    return
  }
  conditionVisible.value = visible
}

watch(
  [panelConditionAttr, () => pageData.value, resolvedFormId],
  () => {
    void evaluatePanelCondition()
  },
  { immediate: true, deep: true },
)

const cols = computed(() => props.data._attributes?.cols || 100)
const panelId = computed(() => +props.data.panelId)

const registerPanelVisibility = (formId: string) => {
  if (!registerPanel || !formId) return
  const instanceKey = tilesInstanceKey()
  registerPanel({
    formId,
    panelId: props.data.panelId,
    instanceKey,
    runtimeId: panelRuntimeId,
    setVisible: (visible: boolean) => {
      visibilityOverride.value = visible
    },
  })
  registeredFormId.value = formId
}
const isOpenDefault = computed(() => {
  // Simple panels are always expanded and cannot be collapsed
  if (props.data._attributes?.style === 'simple') {
    return panelId.value
  }

  if (swingTilesEmbed) {
    const eb = props.data._attributes?.expandByDefault
    if (eb === 'false' || eb === '0') return 0
    return panelId.value
  }

  if(storagePanelsState?.value !== undefined) {
    return storagePanelsState.value ? panelId.value : 0
  }
  // For other styles, use expandByDefault attribute
  return props.data._attributes?.expandByDefault === "true" ? panelId.value : 0
})
const hideHeader = computed(
  () => props.data._attributes?.style === 'simple' || swingTilesEmbed,
)
const isPGroup = computed(() => props.data._attributes?.style === 'pGroup')
const isOverlayPage = computed(() => String(props.pageId).startsWith('overlay-'))
const panelDraggable = computed(() => isPGroup.value && isOverlayPage.value)
const identificators = computed(() => {
  const raw = props.data.identificators
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') return [raw]
  return []
})
const isSimple = computed(
  () => props.data._attributes?.style === 'simple' || swingTilesEmbed,
)
const panelTitle = computed(() => props.data.attrs?.description || '')
const hasIdentificators = computed(() => identificators.value.length > 0)

// Panel attributes
const isReadOnly = computed(() => props.data._attributes?.isReadOnly === "1")
const jsName = computed(() => props.data._attributes?.jsName || props.data.id)

// Panel-level actions
const hasPanelTopActions = computed(() => hasActionsForPositionAndLevel(['top'], 'panel'))
const hasPanelBottomActions = computed(() =>
  hasActionsForPositionAndLevel(['bottom'], 'panel') ||
  hasActionsForPositionAndLevel(['bottom', 'right'], 'panel')
)

// Element-level actions
const hasElementTopActions = computed(() => hasActionsForPositionAndLevel(['top'], 'element'))
const hasElementBottomRightActions = computed(() => hasActionsForPositionAndLevel(['bottom', 'right'], 'element'))

const hasActionsForPositionAndLevel = (position: string[], level: string) => {
  if (!pageActions.value || !props.data.panelId) return false


  const matchingActions = pageActions.value.filter((action: any) => {
    const actionLevel = action.layout?.level?._text
    const actionId = action.layout?.id?._text
    const actionPosition = action.layout?.position?._text

    const levelMatch = actionLevel === level
    const idMatch = actionId === props.data.panelId
    const positionMatch = typeof actionPosition === 'string' && position.includes(actionPosition)

    return levelMatch && idMatch && positionMatch
  })

  return matchingActions.length > 0
}

const updateStoragePanelsState = (val: number | boolean) => {
  const formId = elementContext.formId || resolvedFormId.value || props.data.formId
  const currentPageId = props.pageId
  const currentPanelId = panelId.value

  if (formId && currentPageId && currentPanelId) {
    setPanelSetting(formId, currentPageId, currentPanelId, 'panelOpen', !!val)
  }

  if (isOverlayPage.value) {
    overlayRemeasure?.(!!val)
  }
}

const storagePanelsState = computed(() => {
  const formId = elementContext.formId || resolvedFormId.value || props.data.formId
  const currentPageId = props.pageId
  const currentPanelId = panelId.value

  if (formId && currentPageId && currentPanelId) {
    return getPanelSetting(formId, currentPageId, currentPanelId, 'panelOpen')
  }
  return undefined
})
const panelContentStyle = computed(() => {
  const rows = props.data.rows
  if (!Array.isArray(rows) || rows.length === 0) return {}

  const firstRow = rows[0]
  if (!Array.isArray(firstRow)) return {}

  const columnWidths = firstRow.map((item: any) => {
    const cols = Number(item?._attributes?.cols)
    if (Number.isFinite(cols) && cols > 0) {
      return Math.min(100, cols)
    }
    const widthPct = parseInt(String(item?._attributes?.width ?? '').replace('%', ''), 10)
    if (Number.isFinite(widthPct) && widthPct > 0) {
      return Math.min(100, widthPct)
    }
    return 0
  })
  const widthSum = columnWidths.reduce((a, b) => a + b, 0)
  const normalizedWidths =
    widthSum > 0 && Math.abs(widthSum - 100) > 0.01
      ? columnWidths.map((w) => (100 * w) / widthSum)
      : columnWidths.map((w) => (w > 0 ? w : 100 / Math.max(1, columnWidths.length)))

  // Create CSS custom properties for each column position
  const columnVars: Record<string, string> = {}
  normalizedWidths.forEach((width: number, index: number) => {
    columnVars[`--col-${index}-width`] = `${width}%`
  })

  return columnVars
})

// Заглушки для методов если инъекции не доступны
const stubRegisterPanel = () => console.warn('RegisterPanel not available')
const stubUnRegisterPanel = () => console.warn('UnRegisterPanel not available')

watch(
  () => elementContext.formId,
  (newFormId, oldFormId) => {
    if (!newFormId || newFormId === oldFormId) return
    const { panelId } = props.data
    if (unRegisterPanel && oldFormId) {
      unRegisterPanel({
        panelId,
        formId: oldFormId,
        instanceKey: tilesInstanceKey(),
        runtimeId: panelRuntimeId,
      })
    }
    registerPanelVisibility(newFormId)
  },
  { immediate: true },
)

onMounted(() => {
  const mountFormId = String(
    elementContext.formId || resolvedFormId.value || props.data.formId || '',
  ).trim()
  if (mountFormId) {
    registerPanelVisibility(mountFormId)
  }
  startWorkflowMeasure('Panel.onMounted.scheduleMountListener', {
    panelId: props.data.panelId,
    pageId: props.pageId,
    formId: elementContext.formId,
    rowCount: Array.isArray(props.data.rows) ? props.data.rows.length : 0,
  })()
  // TODO: mn.rumynin - скорректировать по ElementRenderer, тип parseInt(String(id), 10) расположение? в data?
  const { panelId } = props.data
  // не блокировать основной поток листенерами
  setTimeout(() => {
    void (async () => {
      const endMeasure = startWorkflowMeasure('Panel.mountListener', {
        panelId: props.data.panelId,
        pageId: props.pageId,
        formId: elementContext.formId,
        rowCount: Array.isArray(props.data.rows) ? props.data.rows.length : 0,
      })
      try {
        // Вызываем listeners для события mount панели
        if (panelId) {
          await invokeListeners('panel', panelId, 'mount')
        }
      } catch (error) {
        console.error('[Panel.mountListener]', panelId, error)
      } finally {
        endMeasure()
      }
    })()
  }, 0)
})

onUnmounted(async () => {
  const { panelId } = props.data
  const formId = elementContext.formId || registeredFormId.value
  // TODO: mn.rumynin - скорректировать по ElementRenderer
  // Вызываем listeners для события unmount панели
  if (panelId) {
    await invokeListeners('panel', panelId, 'unmount')
  }

  if (unRegisterPanel && formId) {
    unRegisterPanel({
      panelId,
      formId,
      instanceKey: tilesInstanceKey(),
      runtimeId: panelRuntimeId,
    })
  } else {
    stubUnRegisterPanel()
  }
})
</script>

<style scoped>
.panel-grid-item {
  display: flex;
  flex-direction: column;
  min-height: 0;

  &[style*="height: 100%"] {
    :deep(.m-expansion) {
      height: 100%;
      display: flex;
      flex-direction: column;

      .p-accordion {
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .p-accordion-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .p-accordion-content {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .expansion-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
      }
    }

    .panel-content-main {
      flex: 1;
      display: flex;
      flex-direction: column;
      height: 100%;

      .panel-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .elements-row {
        flex: 1;
        display: flex;
        height: 100%;
      }
    }
  }
}
</style>

<style scoped lang="scss">
::v-deep(.is-simple) {
  .p-panel-header {
    display: none;
  }

  .p-panel-content {
    padding: 0;
    border: none;
  }
}

.panel-actions-top {
  margin-bottom: 0.5rem;
}

.panel-actions-bottom {
  margin-top: 0.5rem;
}

.panel-content-main {
  flex: 1;
  min-height: 0;
}

.panel-debug {
  font-size: 12px;

  h6 {
    margin: 0 0 4px 0;
    font-size: 12px;
  }

  p {
    margin: 2px 0;
    font-size: 11px;
  }
}
</style>
